import { ROUTES } from '../routes'

/**
 * Desktop tool policy. Every Studio tool is available inside the desktop
 * shell; the only desktop-specific distinction is *where* a tool executes:
 *  - `local`:  tools with an on-device runtime (Text to Speech, Background
 *              Remover, Image Upscaler, Free Image Generator, Video Frame
 *              Interpolation).
 *  - `remote`: every other tool, which runs against the hosted backend
 *              exactly as on the web.
 *
 * This module is the local-vs-hosted execution policy, not the source of
 * truth for whether a path is a Studio tool — tool-route detection lives in
 * the Studio app (`TOOL_DEFINITIONS`). Built from existing `ROUTES` constants
 * so paths never drift.
 */
export type DesktopToolExecution = 'local' | 'remote'

/** Root routes of tools with an on-device runtime in the desktop shell. */
export const DESKTOP_LOCAL_TOOL_ROUTES: readonly string[] = [
  ROUTES.TEXT_TO_SPEECH.ROOT,
  ROUTES.BACKGROUND_REMOVER.ROOT,
  ROUTES.UPSCALER.ROOT,
  ROUTES.FREE_IMAGE_GENERATOR.ROOT,
  ROUTES.VIDEO_FRAME_INTERPOLATION.ROOT,
] as const

/**
 * True for every route — the desktop shell no longer restricts which tools
 * are reachable. Kept so callers stay stable and a blocklist can be
 * reintroduced here later without touching call sites.
 */
export function isToolRouteAllowedOnDesktop(_route: string): boolean {
  return true
}

/**
 * Execution class for a tool route: `local` for the on-device tools,
 * `remote` for everything else. Callers are expected to pass routes they
 * have already identified as Studio tools.
 */
export function getDesktopToolExecution(route: string): DesktopToolExecution {
  const normalized = normalize(route)
  const isLocal = DESKTOP_LOCAL_TOOL_ROUTES.some(
    root => normalized === normalize(root) || normalized.startsWith(`${normalize(root)}/`),
  )
  return isLocal ? 'local' : 'remote'
}

/**
 * Desktop no longer filters the tool list — the switcher shows the same
 * tools as the web. Kept so call sites stay stable if filtering returns.
 */
export function filterDesktopTools<T extends { route: string }>(
  items: T[],
  _isDesktop: boolean,
): T[] {
  return items
}

function normalize(route: string): string {
  // Drop query/hash and any trailing slash for stable comparison.
  const path = route.split(/[?#]/)[0]
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path
}
